import { Link } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { useOrganization } from '@/hooks/useOrganization'
import { billingDateLabel } from '@/lib/billing'
import { possessive } from '@lib/helpers/format'

/**
 * The strip across the top of the app while an organization is about to lose
 * access: a payment has failed, or its comp is ending. Shown to every
 * organization and venue admin, so the people who notice first are not left to
 * find out on the day; only the billing owner is offered the way to fix it.
 */
export function BillingBanner() {
  const { user } = useAuth()
  const organization = useOrganization().data
  if (!organization || user?.role === 'SUPER_ADMIN') return null
  const { billing } = organization
  const isOwner = organization.billingOwner?.id === user?.id

  let message: string | null = null
  if (billing.state === 'past_due') {
    message = `A payment failed. ${organization.name} will be locked on ${billingDateLabel(billing.graceEndsAt)} unless the card is updated.`
  } else if (billing.state === 'comp_ending' && billing.compEndsAt) {
    message = `${possessive(organization.name)} complimentary plan ends on ${billingDateLabel(billing.compEndsAt)}. A subscription is needed to keep access.`
  }
  if (message === null) return null

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line bg-hold-soft px-4 py-2 text-sm text-hold-ink">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span>{message}</span>
      {isOwner ? (
        <Link to="/billing" className="font-medium underline underline-offset-2">
          Go to billing
        </Link>
      ) : organization.billingOwner ? (
        <span>{organization.billingOwner.name} manages billing.</span>
      ) : null}
    </div>
  )
}
